/**
 * StatusPill — pill-shaped badge with semantic color variants.
 *
 * Single responsibility: render a short status label with a CSK design-token
 * color treatment. Shared security and governance surfaces use it to
 * keep short status labels consistent without duplicating badge styling.
 *
 * Composition: delegates rendering to the generic `Pill` primitive, supplying
 * the security-surface tone vocabulary (safe / required / warn / neutral) and
 * the uppercase, lettered, bordered visual treatment that distinguishes
 * status pills from the plain inline pills used elsewhere in the demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";
import { Pill } from "./Pill.js";

/**
 * The semantic intent of a status pill.
 *
 * - `safe`: the property holds by construction (green).
 * - `required`: a witness is required to proceed (blue).
 * - `warn`: an unguarded path is constructable (red).
 * - `neutral`: informational; no action implied (gray).
 */
export type StatusPillTone = "safe" | "required" | "warn" | "neutral";

export interface StatusPillProps {
  readonly tone: StatusPillTone;
  readonly label: string;
  /** Additional Tailwind class names appended after the defaults. */
  readonly className?: string;
}

interface StatusPalette {
  readonly bg: string;
  readonly border: string;
  readonly text: string;
}

const TONE_PALETTE: Readonly<Record<StatusPillTone, StatusPalette>> = {
  safe: { bg: SAFE[100], border: SAFE[300], text: SAFE[700] },
  required: { bg: PRIMARY[100], border: PRIMARY[300], text: PRIMARY[800] },
  warn: { bg: WARN[100], border: WARN[300], text: WARN[700] },
  neutral: { bg: GRAY[100], border: GRAY[300], text: GRAY[700] },
} as const;

/**
 * Status-pill specific typography (uppercase + tracking + weight).
 *
 * Kept as Tailwind utility classes only — no inline-style mirror. Inline
 * styles win over classes, so duplicating the same concerns inline would
 * make `font-bold`/`uppercase` impossible to override per call site
 * through the `className` passthrough.
 */
const STATUS_CLASSES = "uppercase tracking-wide font-bold leading-tight";

/**
 * Compact pill badge sized for inline status next to a row title.
 *
 * The component is `React.memo`-wrapped because parents typically render
 * many pills inside lists derived from snapshot props that may re-render
 * on unrelated state changes.
 */
export const StatusPill = React.memo(function StatusPill({
  tone,
  label,
  className,
}: StatusPillProps): React.JSX.Element {
  const palette = TONE_PALETTE[tone];
  return (
    <Pill
      color={palette.text}
      bg={palette.bg}
      border={palette.border}
      size="sm"
      className={className === undefined ? STATUS_CLASSES : `${STATUS_CLASSES} ${className}`}
    >
      {label}
    </Pill>
  );
});
